import React from "react";
import Button from "../../../components/buttons/Button";
import { BookOpenIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { TExam, TSub } from "./ExamsConfigs";

type TProps = {
  selExam: TExam | null;
  onAddSubject: () => void;
  onUpdateSubject: (sub: TSub) => void;
  onDeleteSubject: (id: string, e: React.MouseEvent) => void;
  onViewSubject: (sub: TSub) => void;
};

const ExamsMainContent = ({
  selExam,
  onAddSubject,
  onUpdateSubject,
  onDeleteSubject,
  onViewSubject,
}: TProps) => {
  return (
    <div className="flex-1 min-w-0">
      {selExam ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden h-full flex flex-col">
          <div className="p-6 border-b border-gray-50 flex items-center justify-between bg-gray-50/30">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{selExam.name}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {selExam.subjects.length} Subjects configured for this exam
              </p>
            </div>

            <Button
              title="Add Subject"
              onClick={onAddSubject}
              className="!py-2 !px-4"
            />
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {selExam.subjects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selExam.subjects.map((sub) => (
                  <div
                    key={sub._id}
                    onClick={() => onViewSubject(sub)}
                    className="group p-4 bg-white border border-gray-100 rounded-xl hover:border-primary/30 hover:shadow-md transition-all duration-200 cursor-pointer relative"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                          <BookOpenIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-800 group-hover:text-primary transition-colors">
                            {sub.name}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {sub.units?.length || 0} Units
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="p-1.5 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateSubject(sub);
                          }}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          onClick={(e) => onDeleteSubject(sub._id, e)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-4">
                <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center">
                  <BookOpenIcon className="w-8 h-8" />
                </div>
                <div className="text-center">
                  <p className="font-medium text-gray-600">No subjects added yet</p>
                  <p className="text-sm">Click the button above to add your first subject.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="h-full flex flex-col items-center justify-center bg-gray-50/50 rounded-2xl border-2 border-dashed border-gray-200">
          <MagnifyingGlassIcon className="w-12 h-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-600">No Exam Selected</h3>
          <p className="text-gray-400">Select an exam from the sidebar to view its subjects.</p>
        </div>
      )}
    </div>
  );
};

export default ExamsMainContent;
